import { useState, useEffect, useCallback, useRef } from "react";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function randomNonce() {
  return (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
}

export function useGoogleAuth(onToken) {
  const [ready, setReady] = useState(false);
  const popupHandlerRef = useRef(null);

  useEffect(() => {
    function initGsi() {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => onToken(resp.credential),
      });
      setReady(true);
    }

    if (window.google?.accounts) {
      // Script already loaded by index.html — initialize immediately
      initGsi();
    } else {
      // Fallback: wait for the async script already in <head> to finish
      const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (existing) {
        existing.addEventListener("load", initGsi, { once: true });
      } else {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.onload = initGsi;
        document.body.appendChild(script);
      }
    }
  }, [onToken]);

  // Remove popup message listener on unmount
  useEffect(() => {
    return () => {
      if (popupHandlerRef.current) {
        window.removeEventListener("message", popupHandlerRef.current);
        popupHandlerRef.current = null;
      }
    };
  }, []);

  // Fallback for incognito / no Google session: open a plain OAuth popup.
  // The popup redirects back to window.location.origin with #id_token=... in
  // the hash; index.js catches that, posts it back here, and closes the popup.
  const oauthPopupFallback = useCallback(() => {
    // Remove any previous unresolved handler before opening a new popup
    if (popupHandlerRef.current) {
      window.removeEventListener("message", popupHandlerRef.current);
    }
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      response_type: "id_token",
      scope: "openid email profile",
      redirect_uri: window.location.origin,
      nonce: randomNonce(),
      prompt: "select_account",
    });
    const popup = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
      "google-signin",
      "width=500,height=600,left=200,top=100"
    );
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "google-id-token" && event.data.idToken) {
        onToken(event.data.idToken);
        window.removeEventListener("message", handler);
        popupHandlerRef.current = null;
        popup?.close();
      }
    };
    popupHandlerRef.current = handler;
    window.addEventListener("message", handler);
  }, [onToken]);

  const signIn = useCallback(() => {
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        oauthPopupFallback();
      }
    });
  }, [oauthPopupFallback]);

  return { ready, signIn };
}
